import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Handle body scroll lock and escape key
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setActiveModal(null);
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [activeModal]);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="bg-[#FDF8F3] py-12 px-6 border-t border-[#1B2A4A]/10" data-id="element-75">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-[#1B2A4A]/60" data-id="element-76">
          <p data-id="element-77">
            © {new Date().getFullYear()} La Ruta Clara. Todos los derechos
            reservados.
          </p>
          <div className="flex gap-6" data-id="element-78">
            <button onClick={() => setActiveModal('terms')} className="hover:text-[#1B2A4A] transition-colors bg-transparent border-none cursor-pointer" data-id="element-79">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-[#1B2A4A] transition-colors bg-transparent border-none cursor-pointer" data-id="element-80">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity duration-300" onClick={closeModal} role="dialog" aria-modal="true" data-id="element-81">
          {/* Modal Content */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden relative flex flex-col animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-82">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10" data-id="element-83">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A]" data-id="element-84">
                {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
              </h2>
              <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-[#1B2A4A]" aria-label="Cerrar" data-id="element-85">
                <X className="w-6 h-6" data-id="element-86" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-6 md:p-10 overflow-y-auto text-[#1B2A4A]/80 leading-relaxed space-y-6" data-id="element-87">
              {activeModal === 'terms' ? <TermsContent data-id="element-88" /> : <PrivacyContent data-id="element-89" />}
            </div>
          </div>
        </div>}
    </>;
}
function TermsContent() {
  return <div className="space-y-6" data-id="element-90">
      <p className="text-sm text-gray-500 italic" data-id="element-91">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-92">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-93">
          1. Introducción
        </h3>
        <p data-id="element-94">
          Bienvenido a La Ruta Clara. Al comprar y descargar nuestros productos
          digitales (guías PDF, imprimibles y materiales complementarios), usted
          acepta cumplir con estos Términos y Condiciones. Si no está de acuerdo
          con alguna parte de estos términos, le rogamos que no utilice nuestros
          servicios.
        </p>
      </section>

      <section data-id="element-95">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-96">
          2. Licencia de Uso
        </h3>
        <p data-id="element-97">
          Al adquirir "La Ruta Clara", se le otorga una licencia limitada, no
          exclusiva e intransferible para uso personal y no comercial.
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-2" data-id="element-98">
          <li data-id="element-99">
            Puede descargar y almacenar el archivo en sus dispositivos
            personales.
          </li>
          <li data-id="element-100">Puede imprimir copias para su uso personal en el hogar.</li>
          <li data-id="element-101">
            <strong data-id="element-102">No puede</strong> compartir, redistribuir, revender ni
            modificar el contenido para fines comerciales.
          </li>
          <li data-id="element-103">
            <strong data-id="element-104">No puede</strong> subir el archivo a sitios web públicos o
            compartidos.
          </li>
        </ul>
      </section>

      <section data-id="element-105">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-106">
          3. Propiedad Intelectual
        </h3>
        <p data-id="element-107">
          Todo el contenido incluido en La Ruta Clara (textos, gráficos,
          logotipos, imágenes y compilaciones de datos) es propiedad exclusiva
          de La Ruta Clara y está protegido por las leyes de derechos de autor
          internacionales.
        </p>
      </section>

      <section data-id="element-108">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-109">
          4. Política de Reembolso
        </h3>
        <p data-id="element-110">
          Debido a la naturaleza digital de nuestros productos, que se pueden
          descargar y copiar inmediatamente después de la compra, generalmente
          no ofrecemos reembolsos una vez que se ha completado la descarga,
          salvo que la ley local exija lo contrario o en casos de archivos
          defectuosos que no podamos reemplazar.
        </p>
      </section>

      <section data-id="element-111">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-112">
          5. Descargo de Responsabilidad
        </h3>
        <p data-id="element-113">
          La información proporcionada en La Ruta Clara tiene fines educativos e
          informativos únicamente. No sustituye el consejo, diagnóstico o
          tratamiento profesional.
        </p>
        <p className="mt-2" data-id="element-114">
          Si tiene preocupaciones sobre el desarrollo o la salud de su hijo,
          consulte siempre a un pediatra, psicólogo infantil u otro proveedor de
          salud calificado. El uso de la información proporcionada es bajo su
          propio riesgo.
        </p>
      </section>
    </div>;
}
function PrivacyContent() {
  return <div className="space-y-6" data-id="element-115">
      <p className="text-sm text-gray-500 italic" data-id="element-116">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-117">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-118">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-119">
          Recopilamos la información necesaria para procesar su pedido y
          entregarle el producto digital. Esto incluye:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-2" data-id="element-120">
          <li data-id="element-121">Nombre y apellidos.</li>
          <li data-id="element-122">
            Dirección de correo electrónico (para el envío del enlace de
            descarga).
          </li>
          <li data-id="element-123">
            Información de pago (procesada de forma segura por nuestros
            proveedores de pago; no almacenamos detalles completos de tarjetas
            de crédito).
          </li>
        </ul>
      </section>

      <section data-id="element-124">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-125">
          2. Uso de la Información
        </h3>
        <p data-id="element-126">Utilizamos su información para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-2" data-id="element-127">
          <li data-id="element-128">Procesar y entregar su pedido digital.</li>
          <li data-id="element-129">Enviarle confirmaciones de compra y recibos.</li>
          <li data-id="element-130">Responder a sus consultas de soporte al cliente.</li>
          <li data-id="element-131">
            Enviarle actualizaciones sobre el producto o boletines informativos
            (solo si ha dado su consentimiento explícito).
          </li>
        </ul>
      </section>

      <section data-id="element-132">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-133">
          3. Protección de Datos
        </h3>
        <p data-id="element-134">
          Implementamos medidas de seguridad razonables para proteger su
          información personal contra acceso no autorizado, alteración,
          divulgación o destrucción. Utilizamos proveedores de pago certificados
          que cumplen con los estándares de seguridad de la industria (PCI-DSS).
        </p>
      </section>

      <section data-id="element-135">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-136">
          4. Cookies
        </h3>
        <p data-id="element-137">
          Nuestro sitio web utiliza cookies para mejorar su experiencia de
          navegación y analizar el tráfico del sitio. Puede configurar su
          navegador para rechazar todas las cookies o para que le avise cuando
          se envíe una cookie.
        </p>
      </section>

      <section data-id="element-138">
        <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-3" data-id="element-139">
          5. Sus Derechos
        </h3>
        <p data-id="element-140">
          Usted tiene derecho a acceder, corregir o eliminar su información
          personal que tenemos almacenada. Si desea ejercer estos derechos o
          tiene preguntas sobre nuestra política de privacidad, puede
          contactarnos a través de los canales oficiales proporcionados en la
          confirmación de su compra.
        </p>
      </section>
    </div>;
}